import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Plan } from '@/types';
import { useProfile } from './useProfile';

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const { data } = await api.get<{ plans: Plan[] }>('/api/billing/plans');
      return data.plans;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function usePlan() {
  const { data: plans, isLoading: plansLoading } = usePlans();
  const { data: profileData, isLoading: profileLoading } = useProfile();

  const currentPlan = plans?.find((p) => p.id === profileData?.profile?.plan_type) || null;

  return {
    plan: currentPlan,
    plans: plans || [],
    isLoading: plansLoading || profileLoading,
    // Convenience helpers
    canCreateEndpoint: (currentCount: number) =>
      currentPlan ? currentCount < currentPlan.max_endpoints : false,
    canConnect: (currentCount: number) =>
      currentPlan ? currentCount < currentPlan.max_connections : false,
    hasFeature: (feature: 'bulk_replay' | 'custom_templates' | 'allowed_sources' | 'topup_overage') => {
      if (!currentPlan) return false;
      switch (feature) {
        case 'bulk_replay': return currentPlan.feature_bulk_replay;
        case 'custom_templates': return currentPlan.feature_custom_templates;
        case 'allowed_sources': return currentPlan.feature_allowed_sources;
        case 'topup_overage': return currentPlan.feature_topup_overage;
        default: return false;
      }
    },
    limits: currentPlan ? {
      maxEndpoints: currentPlan.max_endpoints,
      maxConnections: currentPlan.max_connections,
      weeklyRequests: currentPlan.weekly_request_limit,
      weeklyBandwidthBytes: currentPlan.weekly_bandwidth_limit_bytes,
      logRetentionDays: currentPlan.log_retention_days,
      maxDevelopers: currentPlan.max_developers,
    } : null,
  };
}

export function useSubscription() {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data } = await api.get<{
        plan: Plan;
        subscriptionStatus: string | null;
        hasSubscription: boolean;
        weeklyRequestLimitOverride: number | null;
        overageLimitCents: number;
        overageUsedCents: number;
      }>('/api/billing/subscription');
      return data;
    },
  });
}
